var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import unset from 'lodash/unset';
import { FormContext } from '../@common/form-context';
import { FieldType } from '../@common/types';
import { toFlatErrors } from './form.utils';
var parseBoolean = function (value) { return value === 'on'; };
var parseNumber = function (value) { return parseInt(value, 10); };
var parseDefault = function (value) { return (value === '' ? null : value); };
var toArray = function (value) {
    if (typeof value === 'string') {
        return [value];
    }
    if (Array.isArray(value)) {
        return value;
    }
    return [];
};
var getValueByType = new Map([
    [FieldType.BOOLEAN, parseBoolean],
    [FieldType.NUMBER, parseNumber],
    [FieldType.SELECT_MULTIPLE, toArray],
]);
var getItemsFromDefVal = function (_, i) { return i; };
/**
 * Форма - элемент взаимодействия пользователя с сайтом или приложением
 *
 * Простейший вариант формы выглядит следующим образом: мы используем элементы <input name="name"/> и <button type="submit">Submit</button>, предварительно импортировав ее из библиотеки <a href ='https://github.com/altiore/form'>@altiore/form</a>.
 */
export var Form = function (_a) {
    var children = _a.children, defaultValues = _a.defaultValues, onSubmit = _a.onSubmit, props = __rest(_a, ["children", "defaultValues", "onSubmit"]);
    var formRef = useRef(null);
    var _b = useState({}), fields = _b[0], setFields = _b[1];
    var _c = useState(false), isSubmitting = _c[0], setIsSubmitting = _c[1];
    var setErrors = useCallback(function (fieldName, errors) {
        setFields(function (s) {
            var _a;
            if (!s[fieldName]) {
                // этот код, похоже, никогда не выполняется и здесь лишь для совместимости
                return s;
            }
            var fieldData = __assign(__assign({}, s[fieldName]), { error: errors === null || errors === void 0 ? void 0 : errors[0], errors: errors, isInvalid: Boolean(errors === null || errors === void 0 ? void 0 : errors.length), isUntouched: false });
            // улучает производительность, избегая рендера, если ошибки не изменились
            if (isEqual(s[fieldName], fieldData)) {
                return s;
            }
            return __assign(__assign({}, s), (_a = {}, _a[fieldName] = fieldData, _a));
        });
    }, [setFields]);
    var setNestedErrors = useCallback(function (errors) {
        toFlatErrors(errors, setErrors);
    }, [setErrors]);
    var setItems = useCallback(function (fieldName, setItemsArg, errors, defaultValue) {
        setFields(function (s) {
            var _a;
            return (__assign(__assign({}, s), (_a = {}, _a[fieldName] = __assign(__assign({}, s[fieldName]), { defaultValue: defaultValue, error: errors === null || errors === void 0 ? void 0 : errors[0], errors: errors, isInvalid: Boolean(errors === null || errors === void 0 ? void 0 : errors.length), isUntouched: false, items: setItemsArg(s[fieldName].items) }), _a)));
        });
    }, [setFields]);
    var registerField = useCallback(function (fieldName, fieldType, fieldDefaultValue) {
        setFields(function (s) {
            var _a;
            var _b, _c;
            var fieldNameArr = fieldName.split('.');
            var dynamicDefault = undefined;
            var fieldNameArrLength = fieldNameArr.length;
            if (fieldNameArrLength > 2) {
                var defValueFromPar = (_b = s === null || s === void 0 ? void 0 : s[fieldNameArr.slice(0, fieldNameArrLength - 2).join('.')]) === null || _b === void 0 ? void 0 : _b.defaultValue;
                dynamicDefault = Array.isArray(defValueFromPar)
                    ? undefined
                    : defValueFromPar
                        ? (defValueFromPar === null || defValueFromPar === void 0 ? void 0 : defValueFromPar[fieldNameArr[fieldNameArrLength - 1]]) || ' '
                        : undefined;
            }
            var defaultValue = (_c = dynamicDefault !== null && dynamicDefault !== void 0 ? dynamicDefault : get(defaultValues, fieldNameArr)) !== null && _c !== void 0 ? _c : fieldDefaultValue;
            return __assign(__assign({}, s), (_a = {}, _a[fieldName] = {
                defaultValue: defaultValue,
                error: undefined,
                errors: [],
                isInvalid: false,
                // Этот флаг работает только для полей у которых есть валидаторы
                isUntouched: true,
                items: fieldType === FieldType.ARRAY
                    ? defaultValue
                        ? defaultValue.map(getItemsFromDefVal)
                        : []
                    : undefined,
                name: fieldName,
                setErrors: setErrors.bind({}, fieldName),
                type: fieldType,
            }, _a));
        });
        return function () {
            setFields(function (s) {
                var _a;
                var newState = __assign(__assign({}, s), (_a = {}, _a[fieldName] = undefined, _a));
                delete newState[fieldName];
                return newState;
            });
        };
    }, [defaultValues, setFields]);
    var handleSubmit = useCallback(function (evt) {
        var _a;
        var localSubmitFunc = typeof evt === 'function' ? evt : onSubmit;
        if (evt === null || evt === void 0 ? void 0 : evt.preventDefault) {
            evt.preventDefault();
        }
        var formData = new window.FormData((_a = formRef.current) !== null && _a !== void 0 ? _a : undefined);
        var values = {};
        var fewValues = [];
        formData.forEach(function (value, name) {
            var prevValue = get(values, name);
            if (prevValue) {
                if (fewValues.length === 0) {
                    fewValues.push(prevValue);
                }
                fewValues.push(value);
                set(values, name, fewValues);
            }
            else {
                set(values, name, value);
            }
        });
        var resValues = cloneDeep(values);
        Object.keys(fields)
            .reverse()
            .forEach(function (fieldKey) {
            var items = fields[fieldKey].items;
            var fieldType = fields[fieldKey].type;
            if (Array.isArray(items)) {
                var value = items.map(function (index) {
                    return cloneDeep(get(resValues, fieldKey))[index];
                });
                unset(resValues, fieldKey);
                set(resValues, fieldKey, value);
            }
            else {
                var prepareValue = getValueByType.get(fieldType) || parseDefault;
                if (prepareValue) {
                    var typedValue = prepareValue(get(resValues, fieldKey));
                    set(resValues, fieldKey, typedValue);
                }
            }
        });
        setIsSubmitting(true);
        Promise.resolve(localSubmitFunc(resValues, setNestedErrors, evt))
            .then(function () {
            setIsSubmitting(false);
        })
            .catch(console.error);
    }, [fields, onSubmit, setIsSubmitting, setNestedErrors]);
    return (React.createElement("form", __assign({}, props, { onSubmit: handleSubmit, ref: formRef }),
        React.createElement(FormContext.Provider, { value: {
                fields: fields,
                formRef: formRef,
                isSubmitting: isSubmitting,
                onSubmit: handleSubmit,
                registerField: registerField,
                setItems: setItems,
            } }, children)));
};
